import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,useLayoutEffect
} from "react";
import { createPortal } from "react-dom";
import NewAnnotation from "../dicomViewer/NewAnnotation";
import AnnotationMarkers from "../dicomViewer/AnnotationMarkers"; // <-- Fixed import
import {
  RenderingEngine,
  Enums,
  init as csCoreInit,
} from "@cornerstonejs/core";
import {
  init as csToolsInit,
  ToolGroupManager,
  StackScrollTool,
  ZoomTool,
  PanTool,
  LengthTool,
  ArrowAnnotateTool,
  RectangleROITool,
  ProbeTool,
  Enums as ToolsEnums,
  addTool,
} from "@cornerstonejs/tools";
import dicomParser from "dicom-parser";
import cornerstoneDICOMImageLoader, {
  init as dicomImageLoaderInit,
  wadouri,
} from "@cornerstonejs/dicom-image-loader";
import { annotation } from "@cornerstonejs/tools";
import SliceNavigator from "./SliceNavigator";
import { useViewer } from "./context/ViewerContext";
import { Enums as CoreEnums } from "@cornerstonejs/core";
import { drawOverlayPure } from "./utils/drawOverlay_l";
import spinner from "../../../assets/spiner.svg";


// --- shared Cornerstone engine (singleton to avoid WebGL context loss) ---
let sharedEngine = null;
function getSharedEngine() {
  if (!sharedEngine || sharedEngine.hasBeenDestroyed) {
    sharedEngine = new RenderingEngine("shared-engine");
  }
  return sharedEngine;
}

export default function DICOMViewer({
  examData,
  currentUser,
  currentImageIndex,
  setCurrentImageIndex,
  notesOpen,
  singleViewport = false, 
  localZoom
}) {
  const {
    activeTool,
    zoom,
    annotationsData,
    setAnnotationsData,
    addAnnotationToData,
    upsertAnnotationInData,
    selectedAnnotationId,
    setSelectedAnnotationId,
    zoomTarget,
    setZoomTarget,
    isSplit,
    setIsSplit,
    setImageIdsCTX,
    setExamData,
    setViewportRefs,
    setNotesOpen,
      setContextMeta,
      setExamJson,
      setActiveTool,
      setZoom,
      lastPrimaryTool,
      setLastPrimaryTool,
      scrollLinked,
    } = useViewer();

  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const leftViewportRef = useRef(null);
  const rightViewportRef = useRef(null);
  const engineRef = useRef(null);
  const toolGroupRef = useRef(null);
  const wheelTimeoutRef = useRef(null);
  const displayedIndexRef = useRef(currentImageIndex);
  const baseScaleRef = useRef(null);
  const lastPrimaryToolRef = useRef("MouseIcon");
  const commandRef = useRef(false); // for the "c+0/1/2" shortcuts
  const scrollLinkedRef = useRef(scrollLinked);

  const [imageIds, setImageIds] = useState([]);
  const [meta, setMeta] = useState(null);
  const [pendingMeasurement, setPendingMeasurement] = useState(false);
  const [annotationData, setAnnotationData] = useState(null);
  const [showProbeOnly, setShowProbeOnly] = useState(false);
  const showProbeOnlyRef = useRef(false);
  const [hasPendingMeasurement, setHasPendingMeasurement] = useState(false);
  const [loadingViewport, setLoadingViewport] = useState(false);
  const [latestMeasurement, setLatestMeasurement] = useState(null);
  const [viewportStable, setViewportStable] = useState(true);
  const effectiveZoom = localZoom ?? zoom;
  // ------- Unique IDs per component instance (multi-viewport safe) -------
  const uid = useMemo(
    () =>
      `${examData?.caseId || "case"}-${Math.random().toString(36).slice(2, 8)}`,
    [examData?.caseId]
  );
  const RENDER_ENGINE_ID = `eng-${uid}`;
  const VP_LEFT_ID = `vpL-${uid}`;
  const VP_RIGHT_ID = `vpR-${uid}`;
  const TG_LEFT_ID = `tgL-${uid}`;
  const TG_RIGHT_ID = `tgR-${uid}`;

  const cornerstoneInitializedRef = useRef(false);

  useEffect(() => {
    if (cornerstoneInitializedRef.current) return;
  
    cornerstoneInitializedRef.current = true;
    (async () => {
      try {
        await csCoreInit();
        await csToolsInit();
        dicomImageLoaderInit();
        addTool(StackScrollTool);
        addTool(ZoomTool);
        addTool(PanTool);
        addTool(LengthTool);
        addTool(ArrowAnnotateTool);
        addTool(RectangleROITool);
        addTool(ProbeTool);
      } catch (error) {
        console.error("Cornerstone initialization failed:", error);
        cornerstoneInitializedRef.current = false; // Allow retry
      }
    })();
  }, []);
  

  useEffect(() => {
    displayedIndexRef.current = currentImageIndex;
  }, [currentImageIndex]);

  const handlePlusButtonClick = () => {
    if (!latestMeasurement) return;
    const { start, end, length, slice, data } = latestMeasurement;

    const annotationObj = {
      id: data.annotationUID,
      start: { x: start[0], y: start[1] },
      end: { x: end[0], y: end[1] },
      midpoint: {
        x: (start[0] + end[0]) / 2,
        y: (start[1] + end[1]) / 2,
      },
      length,
      slice,
      note: "",
      creator: currentUser?.name || "Unknown",
      updatedAt: new Date().toISOString(),
      _pending: true,
    };

    setAnnotationData(latestMeasurement);
    setPendingMeasurement(true);
    setHasPendingMeasurement(false);
  };

  // Overlay
  const drawOverlay = useCallback(
    (viewport, sliceIndex) => {
      if (!meta || !examData?.jsonData || !overlayCanvasRef.current) return;

      const { centers } =
        drawOverlayPure({
          viewport,
          sliceIndex,
          meta,
          examData,
          canvas: overlayCanvasRef.current,
          showProbeOnly: showProbeOnlyRef.current,
        }) || {};

      if (centers?.length) {
        setAnnotationsData((prev) => {
          if (!prev) return prev;
          const list = prev[sliceIndex];
          if (!Array.isArray(list) || list.length === 0) return prev;

          const byId = new Map(centers.map((c) => [c.originalId, c]));
          const nextList = list.map((a) => {
            if (a.type === "finding" && byId.has(a.originalId)) {
              const c = byId.get(a.originalId);
              return { ...a, x: c.x, y: c.y };
            }
            return a;
          });

          let changed = false;
          for (let i = 0; i < list.length; i++) {
            if (list[i] !== nextList[i]) {
              changed = true;
              break;
            }
          }
          if (!changed) return prev;
          return { ...prev, [sliceIndex]: nextList };
        });
      }
    },
    [meta, examData?.jsonData, setAnnotationsData]
  );

  // Update both viewports to currentImageIndex
  const updateViewports = useCallback(() => {
    const leftVP = leftViewportRef.current;
    const rightVP = rightViewportRef.current;

    return Promise.all([
      leftVP?.setImageIdIndex(currentImageIndex).then(() => leftVP.render()),
      rightVP?.setImageIdIndex(currentImageIndex).then(() => rightVP.render()),
    ]).then(() => {
      if (leftVP && rightVP) {
        const leftCam = leftVP.getCamera();
        rightVP.setCamera({
          ...rightVP.getCamera(),
          parallelScale: leftCam.parallelScale,
          focalPoint: [...leftCam.focalPoint],
          position: [...leftCam.position],
        });
        rightVP.render();
      }
      requestAnimationFrame(() => {
        drawOverlay(rightVP, currentImageIndex);
      });
    });
  }, [drawOverlay, currentImageIndex]);

  const lastCaseIdRef = useRef(null);

  // Keep scrollLinkedRef in sync with context value
  useEffect(() => {
    scrollLinkedRef.current = scrollLinked;
  }, [scrollLinked]);

  useLayoutEffect(() => {
    if (!examData) return;
  
    const caseId = examData.caseId ?? examData.accessionNumber;
    if (!caseId) return;
  
    // Only trigger when the active case really changes (tab switch)
    if (lastCaseIdRef.current !== caseId) {
      lastCaseIdRef.current = caseId;
      setLoadingViewport(true);
      // Hide overlay initially to prevent flicker
      if (overlayCanvasRef.current) {
        overlayCanvasRef.current.style.opacity = '0';
      }
    }
  }, [examData?.caseId, examData?.accessionNumber, examData]);
  

  // Smooth wheel (keeps your original, non-flicker behaviour)
  const handleWheelDebounced = useCallback(
    (evt) => {
      evt.preventDefault();
      const delta = evt.deltaY > 0 ? 1 : -1;
      const newIndex = Math.min(
        Math.max(0, displayedIndexRef.current + delta),
        imageIds.length - 1
      );

      if (newIndex !== displayedIndexRef.current) {
        displayedIndexRef.current = newIndex;

        const leftVP = leftViewportRef.current;
        const rightVP = rightViewportRef.current;

        if (leftVP && rightVP) {
          leftVP.setImageIdIndex(newIndex);
          rightVP.setImageIdIndex(newIndex);
          leftVP.render();
          rightVP.render();

          const leftCam = leftVP.getCamera();
          rightVP.setCamera({
            ...rightVP.getCamera(),
            parallelScale: leftCam.parallelScale,
            focalPoint: [...leftCam.focalPoint],
            position: [...leftCam.position],
          });
          rightVP.render();
        }

        clearTimeout(wheelTimeoutRef.current);
        // If linked scrolling is active, update immediately for synchronized scrolling
        // Otherwise, use debounced update to prevent flicker
        // Use ref to avoid recreating callback when scrollLinked changes
        if (scrollLinkedRef.current) {
          setCurrentImageIndex(newIndex);
        } else {
          wheelTimeoutRef.current = setTimeout(() => {
            setCurrentImageIndex(newIndex);
          }, 100);
        }
      }
    },
    [imageIds.length, setCurrentImageIndex, setZoom, setZoomTarget, meta]
  );

  // Parse first file meta + build imageIds
  useEffect(() => {
    if (!examData?.dicomFiles?.length) return;
    // setLoadingViewport(true);

    (async () => {
      const firstFile = examData.dicomFiles[0];
      const buffer = new Uint8Array(await firstFile.arrayBuffer());
      const ds = dicomParser.parseDicom(buffer);
      const metaLocal = {
        rows: ds.uint16("x00280010"),
        columns: ds.uint16("x00280011"),
        pixelSpacing: ds.string("x00280030")?.split("\\").map(Number) || [1, 1],
        imagePositionPatient: ds
          .string("x00200032")
          ?.split("\\")
          .map(Number) || [0, 0, 0],
        imageOrientationPatient: ds
          .string("x00200037")
          ?.split("\\")
          .map(Number) || [1, 0, 0, 0, 1, 0],
      };

      setMeta(metaLocal);
      setContextMeta(metaLocal);
      setExamData(examData);

      const filesWithInstance = await Promise.all(
        examData.dicomFiles.map(async (file) => {
          const b = await file.arrayBuffer();
          const d = dicomParser.parseDicom(new Uint8Array(b));
          const instanceNumber = d.intString("x00200013");
          return { file, instanceNumber: Number(instanceNumber) || 0 };
        })
      );

      filesWithInstance.sort((a, b) => a.instanceNumber - b.instanceNumber);
      const ids = filesWithInstance.map(({ file }) =>
        wadouri.fileManager.add(file)
      );
      setImageIds(ids);
      setImageIdsCTX(ids);
    })();
  }, [examData, setContextMeta, setExamData, setImageIdsCTX]);

  // Create engine, viewports, tools (with UNIQUE IDs)
  useEffect(() => {
    if (!imageIds.length || !meta) return;

    const engine = getSharedEngine();
    engineRef.current = engine;
    const engineIdForTools = engineRef.current?.id ?? "shared-engine";
    
    // ResizeObserver to handle container size changes
    let resizeObserver = null;

    const ensureViewport = (id, element) => {
      if (!element) return;
      try {
        engine.enableElement({
          viewportId: id,
          type: Enums.ViewportType.STACK,
          element,
        });
      } catch {
        // already enabled; if element changed (react remount), rebind it
        const vp = engine.getViewport(id);
        if (vp && vp.element !== element) {
          engine.disableElement(id);
          engine.enableElement({
            viewportId: id,
            type: Enums.ViewportType.STACK,
            element,
          });
        }
      }
    };

    ensureViewport(VP_LEFT_ID, leftRef.current);
    ensureViewport(VP_RIGHT_ID, rightRef.current);

    const leftVP = engine.getViewport(VP_LEFT_ID);
    const rightVP = engine.getViewport(VP_RIGHT_ID);
    leftViewportRef.current = leftVP;
    rightViewportRef.current = rightVP;

    setViewportRefs({
      leftViewportRef,
      rightViewportRef,
    });

    // Camera sync
    let rafId;
    function handleCameraChange() {
      if (!leftVP || !rightVP) return;

      const leftCam = leftVP.getCamera();
      rightVP.setCamera({
        ...rightVP.getCamera(),
        parallelScale: leftCam.parallelScale,
        focalPoint: [...leftCam.focalPoint],
        position: [...leftCam.position],
      });
      rightVP.render();

      const activeIndex =
        rightVP.getCurrentImageIdIndex?.() ?? displayedIndexRef.current ?? 0;
      rafId = requestAnimationFrame(() => {
        drawOverlay(rightVP, activeIndex);
      });
    }

    if (leftVP?.element) {
      leftVP.element.addEventListener(
        CoreEnums.Events.CAMERA_MODIFIED,
        handleCameraChange
      );
    }

    // Clean any previous groups with the same (unique) IDs in this instance
    try {
      ToolGroupManager.destroyToolGroup(TG_LEFT_ID);
    } catch {}
    try {
      ToolGroupManager.destroyToolGroup(TG_RIGHT_ID);
    } catch {}

    const leftToolGroup = ToolGroupManager.createToolGroup(TG_LEFT_ID);
    const rightToolGroup = ToolGroupManager.createToolGroup(TG_RIGHT_ID);
    toolGroupRef.current = leftToolGroup;

    leftToolGroup.addViewport(VP_LEFT_ID, engine.id);
    rightToolGroup.addViewport(VP_RIGHT_ID, engine.id);

    leftToolGroup.addTool(LengthTool.toolName);
    leftToolGroup.addTool(ArrowAnnotateTool.toolName);
    leftToolGroup.addTool(PanTool.toolName);
    leftToolGroup.addTool(StackScrollTool.toolName);
    leftToolGroup.addTool(ZoomTool.toolName);
    leftToolGroup.addTool(RectangleROITool.toolName);
    leftToolGroup.addTool(ProbeTool.toolName);

    rightToolGroup.addTool(StackScrollTool.toolName);
    rightToolGroup.addTool(ZoomTool.toolName);

    leftToolGroup.setToolPassive(LengthTool.toolName);
    leftToolGroup.setToolPassive(ArrowAnnotateTool.toolName);
    leftToolGroup.setToolPassive(PanTool.toolName);
    leftToolGroup.setToolPassive(RectangleROITool.toolName);
    leftToolGroup.setToolPassive(ProbeTool.toolName);
    leftToolGroup.setToolEnabled(StackScrollTool.toolName);
    leftToolGroup.setToolActive(ZoomTool.toolName, {
      bindings: [{ mouseButton: ToolsEnums.MouseBindings.Secondary }],
    });

    rightToolGroup.setToolEnabled(StackScrollTool.toolName);
    rightToolGroup.setToolActive(ZoomTool.toolName, {
      bindings: [{ mouseButton: ToolsEnums.MouseBindings.Secondary }],
    });
    rightToolGroup.setToolActive(PanTool.toolName, {
      bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
    });

    // Set up ResizeObserver to handle container size changes
    // Debounce resize to prevent flicker during layout changes
    let resizeTimeout = null;
    const containerElement = leftRef.current?.parentElement;
    if (containerElement && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        // Debounce resize calls to prevent flicker
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
        }
        resizeTimeout = setTimeout(() => {
          if (leftVP && rightVP) {
            engine.resize();
            leftVP.resize();
            rightVP.resize();
            leftVP.render();
            rightVP.render();
            
            // Mark viewport as stable after resize completes
            // Use double RAF to ensure render is complete
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                setViewportStable(true);
              });
            });
          }
        }, 100);
      });
      resizeObserver.observe(containerElement);
    }

    Promise.all([
      leftVP.setStack(imageIds, 0).then(() => leftVP.render()),
      rightVP.setStack(imageIds, 0).then(() => rightVP.render()),
    ]).then(() => {
      // Resize viewports to ensure correct dimensions on first load
      // Use double RAF to ensure DOM has final dimensions
      requestAnimationFrame(() => {
        engine.resize();
        leftVP.resize();
        rightVP.resize();
        leftVP.render();
        rightVP.render();
        
        requestAnimationFrame(() => {
          // One more resize after render to catch any layout changes
          engine.resize();
          leftVP.render();
          rightVP.render();
          
          // Reset baseScale after resize so zoom calculations use correct base
          // This ensures the image appears at the same relative size as before
          if (baseScaleRef.current) {
            const camera = leftVP.getCamera();
            baseScaleRef.current = camera.parallelScale;
          }
          
          // Draw overlay and wait for it to be fully painted before hiding spinner
          drawOverlay(rightVP, 0);
          
          // Wait for overlay to be fully painted to screen before hiding spinner
          // This prevents flickering on the right viewport
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Double RAF ensures overlay is fully painted and stable
              // Set overlay opacity to visible via direct style manipulation
              if (overlayCanvasRef.current) {
                overlayCanvasRef.current.style.opacity = '1';
              }
              // One more RAF to ensure opacity change is painted
              requestAnimationFrame(() => {
                // Wait additional 500ms after everything is stable to ensure no flicker
                setTimeout(() => {
                  setLoadingViewport(false);
                }, 500);
              });
            });
          });
        });
      });
    });

    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (leftVP?.element) {
        leftVP.element.removeEventListener(
          CoreEnums.Events.CAMERA_MODIFIED,
          handleCameraChange
        );
      }
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      
      // Cleanup tool groups BEFORE disabling viewports
      try {
        ToolGroupManager.destroyToolGroup(TG_LEFT_ID);
      } catch {}
      try {
        ToolGroupManager.destroyToolGroup(TG_RIGHT_ID);
      } catch {}
      
      try {
        engineRef.current?.disableElement?.(VP_LEFT_ID);
      } catch {}
      try {
        engineRef.current?.disableElement?.(VP_RIGHT_ID);
      } catch {}
    };
  }, [
    imageIds,
    meta,
    singleViewport,
    RENDER_ENGINE_ID,
    VP_LEFT_ID,
    VP_RIGHT_ID,
    TG_LEFT_ID,
    TG_RIGHT_ID,
    setViewportRefs,
    drawOverlay,
  ]);

  // Wheel listeners (unchanged behaviour)
  useEffect(() => {
    if (!imageIds.length) return;

    const leftElement = leftRef.current;
    const rightElement = rightRef.current;

    // Create stable wrapper to ensure proper cleanup
    const stableHandler = (evt) => handleWheelDebounced(evt);

    if (leftElement) {
      leftElement.addEventListener("wheel", stableHandler, {
        passive: false,
        capture: true,
      });
    }
    if (rightElement) {
      rightElement.addEventListener("wheel", stableHandler, {
        passive: false,
        capture: true,
      });
    }

    return () => {
      clearTimeout(wheelTimeoutRef.current);
      
      if (leftElement) {
        leftElement.removeEventListener("wheel", stableHandler, {
          capture: true,
        });
      }
      if (rightElement) {
        rightElement.removeEventListener("wheel", stableHandler, {
          capture: true,
        });
      }
    };
  }, [imageIds.length, handleWheelDebounced]);

  // tool selection
  useEffect(() => {
    const toolGroup = toolGroupRef.current;
    if (!toolGroup) return;

   // helper: set cursor ONLY on the left viewport (container + canvas)
    const setCursor = (css) => {
      const leftEl = leftRef.current;
      if (!leftEl) return;
    
      // container
      leftEl.style.cursor = css || "default";
    
      // Cornerstone draws a <canvas> inside – override its cursor too
      const canvas = leftEl.querySelector("canvas");
      if (canvas) {
        canvas.style.cursor = css || "default";
      }
    };


    // Reset all to passive (clears any previous selection)
    toolGroup.setToolPassive(LengthTool.toolName);
    toolGroup.setToolPassive(ArrowAnnotateTool.toolName);
    toolGroup.setToolPassive(PanTool.toolName);
    toolGroup.setToolPassive(RectangleROITool.toolName);
    toolGroup.setToolPassive(ProbeTool.toolName);

    // Always-on stack scroll and right-click zoom
    toolGroup.setToolEnabled(StackScrollTool.toolName);
    toolGroup.setToolActive(ZoomTool.toolName, {
      bindings: [{ mouseButton: ToolsEnums.MouseBindings.Secondary }],
    });

    // --- Overlay toggles: DO NOT change tool; re-apply the last one ---
    if (activeTool === "ProbeOnly" || activeTool === "AllColors") {
      const probeOn = activeTool === "ProbeOnly";
      // Update ref immediately for synchronous access
      showProbeOnlyRef.current = probeOn;
      
      // Batch state update to prevent flicker - use React's automatic batching
      setShowProbeOnly(probeOn);
      
      // Redraw overlay in next frame to avoid blocking and prevent flicker
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const vp = rightViewportRef.current || leftViewportRef.current;
          if (!vp) return;
          const idx = vp.getCurrentImageIdIndex?.() ?? displayedIndexRef.current;
          // Only redraw overlay canvas, don't trigger viewport renders
          drawOverlay(vp, idx);
        });
      });
      
      // Restore the last primary tool's cursor and tool state
      const toolToUse = lastPrimaryTool || lastPrimaryToolRef.current || "MouseIcon";
      switch (toolToUse) {
        case "MouseIcon":
          setCursor("default");
          break;
        case "HandIcon":
          setCursor("grab");
          toolGroup.setToolActive(PanTool.toolName, {
            bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
          });
          break;
        case "TriangleMeasurementIcon":
          setCursor("crosshair");
          toolGroup.setToolActive(LengthTool.toolName, {
            bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
          });
          break;
        case "RectangleIcon":
          toolGroup.setToolActive(RectangleROITool.toolName, {
            bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
          });
          break;
        default:
          setCursor("default");
          break;
      }
      return;
    }

    // --- MeasurementIcon (filter): Keep the last tool active ---
    if (activeTool === "MeasurementIcon") {
      // Restore the last primary tool's cursor and tool state
      const toolToUse = lastPrimaryTool || lastPrimaryToolRef.current || "MouseIcon";
      switch (toolToUse) {
        case "MouseIcon":
          setCursor("default");
          break;
        case "HandIcon":
          setCursor("grab");
          toolGroup.setToolActive(PanTool.toolName, {
            bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
          });
          break;
        case "TriangleMeasurementIcon":
          setCursor("crosshair");
          toolGroup.setToolActive(LengthTool.toolName, {
            bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
          });
          break;
        case "RectangleIcon":
          toolGroup.setToolActive(RectangleROITool.toolName, {
            bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
          });
          break;
        default:
          setCursor("default");
          break;
      }
      return;
    }

    switch (activeTool) {
      case "MouseIcon":
        // true "regular mouse": no primary binding
        setCursor("default");
        setLastPrimaryTool("MouseIcon");
        lastPrimaryToolRef.current = "MouseIcon";
        break;

        case "HandIcon":
          setCursor("grab");
          toolGroup.setToolActive(PanTool.toolName, {
            bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
          });
          setLastPrimaryTool("HandIcon");
          lastPrimaryToolRef.current = "HandIcon";
          break;

      case "TriangleMeasurementIcon":
        // Let Cornerstone handle the cursor for measurement tool (crosshair)
        setCursor("crosshair");
        toolGroup.setToolActive(LengthTool.toolName, {
          bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
        });
        setLastPrimaryTool("TriangleMeasurementIcon");
        lastPrimaryToolRef.current = "TriangleMeasurementIcon";
        break;

      case "RectangleIcon":
        toolGroup.setToolActive(RectangleROITool.toolName, {
          bindings: [{ mouseButton: ToolsEnums.MouseBindings.Primary }],
        });
        setLastPrimaryTool("RectangleIcon");
        lastPrimaryToolRef.current = "RectangleIcon";
        break;

      default:
        setCursor("default");
        setLastPrimaryTool("MouseIcon");
        lastPrimaryToolRef.current = "MouseIcon";
        break;
    }

    // Only clear pending measurement when leaving the Length tool
    if (activeTool !== "TriangleMeasurementIcon") {
      setHasPendingMeasurement(false);
      setLatestMeasurement(null);
    }
  }, [activeTool, lastPrimaryTool, setLastPrimaryTool]);

  // Hand tool: switch cursor to "grabbing" while mouse is pressed
useEffect(() => {
  if (activeTool !== "HandIcon") return;

  const leftEl = leftRef.current;
  if (!leftEl) return;

  // prefer the internal canvas (actual interaction surface)
  const canvas = leftEl.querySelector("canvas") || leftEl;

  const setGrab = () => {
    leftEl.style.cursor = "grab";
    canvas.style.cursor = "grab";
  };

  const setGrabbing = () => {
    leftEl.style.cursor = "grabbing";
    canvas.style.cursor = "grabbing";
  };

  const handleMouseDown = () => {
    setGrabbing();
  };

  const handleMouseUp = () => {
    setGrab();
  };

  // mouseup might happen outside the viewport → listen on window as well
  canvas.addEventListener("mousedown", handleMouseDown);
  window.addEventListener("mouseup", handleMouseUp);
  canvas.addEventListener("mouseleave", handleMouseUp);

  // ensure we start in "grab" when switching to hand
  setGrab();

  return () => {
    canvas.removeEventListener("mousedown", handleMouseDown);
    window.removeEventListener("mouseup", handleMouseUp);
    canvas.removeEventListener("mouseleave", handleMouseUp);
  };
  }, [activeTool, lastPrimaryTool, setLastPrimaryTool]);


  // capture measurement after 2 clicks
  useEffect(() => {
    if (activeTool !== "TriangleMeasurementIcon") return;

    const canvas = leftRef.current;
    if (!canvas) return;

    let clickCount = 0;

    const handleClick = () => {
      clickCount += 1;

      if (clickCount === 2) {
        const annotations = annotation.state.getAnnotations(
          "Length",
          leftRef.current
        );
        if (!annotations?.length) {
          clickCount = 0;
          return;
        }

        const latest = annotations[annotations.length - 1];

        if (annotations.length > 1) {
          for (let i = 0; i < annotations.length - 1; i++) {
            annotation.state.removeAnnotation(annotations[i].annotationUID);
          }
        }

        const points = latest.data?.handles?.points ?? [];
        const imageId = latest.metadata?.referencedImageId;
        const stats = latest.data?.cachedStats?.[`imageId:${imageId}`];
        const length = stats?.length;

        if (points.length < 2 || !length) {
          clickCount = 0;
          return;
        }

        const measurementData = {
          start: points[0],
          end: points[1],
          length,
          data: latest,
          slice: currentImageIndex, // used here
        };

        setLatestMeasurement(measurementData);
        setAnnotationData(measurementData);
        setPendingMeasurement(true);
        clickCount = 0;
      }
    };

    canvas.addEventListener("click", handleClick);

    return () => {
      canvas.removeEventListener("click", handleClick);
      clickCount = 0;
    };
  }, [activeTool, leftRef, currentImageIndex]);

  // react to external index change
  useEffect(() => {
    if (!imageIds.length) return;
    updateViewports(currentImageIndex);
  }, [currentImageIndex, imageIds, updateViewports]);

  // Reset viewport stability when notesOpen changes to wait for resize
  useEffect(() => {
    if (notesOpen) {
      setViewportStable(false);
      // Wait for layout to change and ResizeObserver to handle resize
      // Use a short delay to ensure resize completes before showing markers
      const timeoutId = setTimeout(() => {
        setViewportStable(true);
      }, 150);
      
      return () => clearTimeout(timeoutId);
    } else {
      // When closing notes, mark stable immediately
      setViewportStable(true);
    }
  }, [notesOpen]);

  // zoom
  useEffect(() => {
    if (!leftViewportRef.current || !rightViewportRef.current || !meta) return;

    const leftVP = leftViewportRef.current;
    const rightVP = rightViewportRef.current;

    // Only initialize baseScale if it's not set
    if (!baseScaleRef.current) {
      const camera = leftVP.getCamera();
      baseScaleRef.current = camera.parallelScale;
    }
    const baseScale = baseScaleRef.current;

    const effectiveZoom = (localZoom ?? zoom) || 1;

    const {
      rows,
      columns,
      pixelSpacing,
      imagePositionPatient,
      imageOrientationPatient,
    } = meta;
    const halfWidth = (columns * pixelSpacing[1]) / 2;
    const halfHeight = (rows * pixelSpacing[0]) / 2;

    const centerWorld = [
      imagePositionPatient[0] +
        halfWidth * imageOrientationPatient[3] +
        halfHeight * imageOrientationPatient[0],
      imagePositionPatient[1] +
        halfWidth * imageOrientationPatient[4] +
        halfHeight * imageOrientationPatient[1],
      imagePositionPatient[2],
    ];

    [leftVP, rightVP].forEach((vp) => {
      const camera = vp.getCamera();
      camera.parallelScale = baseScale / effectiveZoom;

      if (zoomTarget && Array.isArray(zoomTarget) && zoomTarget.length === 3) {
        camera.focalPoint = zoomTarget;
        camera.position = [zoomTarget[0], zoomTarget[1], camera.position[2]];
      } else {
        camera.focalPoint = [...centerWorld];
        camera.position = [centerWorld[0], centerWorld[1], camera.position[2]];
      }

      vp.setCamera(camera);
      vp.render();
    });

    engineRef.current?.resize();

    if (rightViewportRef.current) {
      const activeIndex =
        rightViewportRef.current.getCurrentImageIdIndex?.() ??
        currentImageIndex;
      drawOverlay(rightViewportRef.current, activeIndex);
    }
  }, [localZoom, zoom, zoomTarget, meta]);

  useEffect(() => {
    if (!imageIds.length) return;
  
    const goToSlice = (newIndex) => {
      const maxIndex = imageIds.length - 1;
      const clamped = Math.min(Math.max(newIndex, 0), maxIndex);
  
      if (clamped === displayedIndexRef.current) return;
  
      displayedIndexRef.current = clamped;
  
      const leftVP = leftViewportRef.current;
      const rightVP = rightViewportRef.current;
  
      if (leftVP && rightVP) {
        leftVP.setImageIdIndex(clamped);
        rightVP.setImageIdIndex(clamped);
        leftVP.render();
        rightVP.render();
  
        const leftCam = leftVP.getCamera();
        rightVP.setCamera({
          ...rightVP.getCamera(),
          parallelScale: leftCam.parallelScale,
          focalPoint: [...leftCam.focalPoint],
          position: [...leftCam.position],
        });
        rightVP.render();
  
        const activeIdx =
          rightVP.getCurrentImageIdIndex?.() ?? clamped;
        drawOverlay(rightVP, activeIdx);
      }
  
      setCurrentImageIndex(clamped);
    };
  
    const handleKeyDown = (evt) => {
      const activeEl = document.activeElement;
      if (
        activeEl &&
        (activeEl.tagName === "INPUT" ||
          activeEl.tagName === "TEXTAREA" ||
          activeEl.isContentEditable)
      ) {
        // don't steal keys from inputs
        return;
      }
  
      // "c" acts like a command prefix: c+0 / c+1 / c+2
      if (evt.key === "c" || evt.key === "C") {
        commandRef.current = true;
        return;
      }
  
      if (commandRef.current) {
        // c+0 → zoom to 100%
        if (evt.key === "0") {
          evt.preventDefault();
          setZoom(1);
        }
        // c+1 → zoom to "fit" (same as baseline in this implementation)
        else if (evt.key === "1") {
          evt.preventDefault();
          setZoom(1);
        }
        // c+2 → zoom to a higher level (acts like "zoom to selection" shortcut)
        else if (evt.key === "2") {
          evt.preventDefault();
          setZoom(2); // you can tune this value (e.g. 2.5)
        }
  
        commandRef.current = false;
        return;
      }
  
      switch (evt.key) {
        // ← : scroll back one slice
        case "ArrowLeft": {
          evt.preventDefault();
          goToSlice(displayedIndexRef.current - 1);
          break;
        }
        case "ArrowRight": {
          evt.preventDefault();
          goToSlice(displayedIndexRef.current + 1);
          break;
        }
  
        // + : zoom in (also catch "=" because Shift+"=" is "+")
        case "+":
        case "=": {
          evt.preventDefault();
          setZoom((prev) => {
            const cur = typeof prev === "number" && !Number.isNaN(prev) ? prev : 1;
            const next = Math.min(3, cur + 0.25);
            return Number(next.toFixed(2));
          });
          break;
        }
  
        // - : zoom out
        case "-": {
          evt.preventDefault();
          setZoom((prev) => {
            const cur = typeof prev === "number" && !Number.isNaN(prev) ? prev : 1;
            const next = Math.max(0.25, cur - 0.25);
            return Number(next.toFixed(2));
          });
          break;
        }
  
        default:
          break;
      }
    };
  
    const handleKeyUp = (evt) => {
      if (evt.key === "c" || evt.key === "C") {
        commandRef.current = false;
      }
    };
  
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
  
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [imageIds.length, drawOverlay, setCurrentImageIndex, setZoom]);
  

  return (
    <div className="w-full h-full relative flex items-center justify-center">
      <div className="flex flex-row gap-[10px] w-full h-full items-center justify-center">
        {/* Left Viewer */}
        <div className="relative grow h-full flex items-center justify-center">
          <div className="relative h-full w-full bg-black overflow-hidden">
          {loadingViewport && (
                  <TileSkeleton/>
          )}

            <div
              ref={leftRef}
              className="w-full h-full"
              style={{ touchAction: "none", imageRendering: "auto" }}
            />

            {pendingMeasurement && annotationData && (
              <NewAnnotation
                annotation={annotationData}
                currentUser={currentUser}
                examData={examData}
                leftViewportRef={leftViewportRef.current}
                onClose={() => {
                  setPendingMeasurement(false);
                  setAnnotationData(null);
                }}
              />
            )}

            {notesOpen && (
              <AnnotationMarkers
                annotationData={annotationsData}
                currentImageIndex={currentImageIndex}
                selectedAnnotationId={selectedAnnotationId}
                handleAnnotationSelection={(id) =>
                  setSelectedAnnotationId(
                    id === selectedAnnotationId ? null : id
                  )
                }
                viewport={leftViewportRef.current}
                isReady={!!leftViewportRef.current && viewportStable}
              />
            )}

            {pendingMeasurement && (
              <div className="absolute inset-0 bg-transparent cursor-not-allowed z-40" />
            )}

            {hasPendingMeasurement && (
              <button
                onClick={handlePlusButtonClick}
                className="absolute top-14 left-3 bg-[#5A3EFF] hover:bg-[#4C32E6] text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold shadow-lg transition-colors duration-200 z-60"
                title="Add annotation for measurement">
                +
              </button>
            )}
          </div>
        </div>

        {/* Right Viewer */}
        <div className="relative grow h-full flex items-center justify-center">
          <div className="relative h-full w-full bg-black">
          {loadingViewport && (
                  <TileSkeleton/>
          )}

            <div
              ref={rightRef}
              className="w-full h-full"
              style={{ touchAction: "none", imageRendering: "auto" }}
            />
            <canvas
              ref={overlayCanvasRef}
              className="absolute top-0 left-0 pointer-events-none w-full h-full"
              style={{ 
                imageRendering: "auto",
                width: "100%",
                height: "100%",
                // opacity is controlled via direct DOM manipulation (overlayCanvasRef.current.style.opacity)
                // to avoid React state update timing issues
              }}
            />
            {pendingMeasurement && (
              <div className="absolute inset-0 bg-transparent cursor-not-allowed z-40" />
            )}
          </div>
        </div>
      </div>

      {/* Slice Navigator */}
      {imageIds.length > 0 && (
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-3 py-1 rounded text-sm font-mono z-50">
          <SliceNavigator
            currentIndex={currentImageIndex +1}
            totalSlices={imageIds.length}
            onChange={(newIndex) => {
              setCurrentImageIndex(newIndex);
            }}
          />
        </div>
      )}
    </div>
  );
}


function TileSkeleton({ label = "Loading…" }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center z-[9999] bg-black">
      <div className="flex flex-col items-center gap-2 text-gray-300 animate-pulse">
        <img
          src={spinner}
          alt="Loading…"
          className="h-16 w-16 animate-spin filter invert block"
        />
        <div className="text-sm">{label}</div>
      </div>
    </div>
  );
}